import { Box, Dialog, DialogTitle, DialogContent, Typography, Button, DialogActions } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CloseDialogAction } from '../../../components/shared/dialogs/CloseDialogAction';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import { useState } from 'react';

export interface DateSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedDate: string) => void;
  selectedItemsCount: number;
  dateType: 'cutoff' | 'delivery';
  title?: string;
}

function DateSelectionModal(props: DateSelectionModalProps) {
  const { t } = useTranslation('odsmanagement');
  const [selectedDate, setSelectedDate] = useState<string>('');
  
  const handleClose = () => {
    setSelectedDate('');
    props.onClose();
  };

  const handleConfirm = () => {
    if (selectedDate) {
      props.onConfirm(selectedDate);
      setSelectedDate('');
    }
  };

  const getTitle = () => {
    if (props.title) return props.title;
    return props.dateType === 'cutoff' 
      ? t('setCutOffDateForSelectedItems')
      : t('setDeliveryDateForSelectedItems');
  };

  const getDateLabel = () => {
    return props.dateType === 'cutoff' 
      ? t('cutOffDate')
      : t('deliveryDate');
  };

  const getConfirmButtonText = () => {
    return props.dateType === 'cutoff' 
      ? t('setCutOffDate')
      : t('setDeliveryDate');
  };

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      onClose={handleClose}
      aria-labelledby="date-selection-dialog"
      open={props.open}
    >
      <DialogTitle>
        {getTitle()}
        <CloseDialogAction onCancel={handleClose} />
      </DialogTitle>
      <DialogContent sx={{ pt: 3, pb: 3 }}>
        <Box sx={{ textAlign: 'left', mb: 3 }}>
          <Typography variant="textSM" 
            color="text.secondary">
            {t('selectedItemsCount', { count: props.selectedItemsCount })}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'left' }}>
          <Box sx={{ width: '100%' }}>
            <DatePickerLocalized
              useCalendar={false}
              format="DD/MM/YYYY"
              label={getDateLabel()}
              value={selectedDate}
              shouldDisableDate={(date) => {
                const d = new Date(date);
                d.setHours(0, 0, 0, 0);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const tomorrow = new Date(today);
                tomorrow.setDate(today.getDate() + 1);

                return  d < tomorrow;
              }}
              translations={{
                clearButtonLabel: t('calendar.clearButtonLabel'),
                todayButtonLabel: t('calendar.todayButtonLabel'),
                cancelButtonLabel: t('calendar.cancelButtonLabel'),
                okButtonLabel: t('calendar.okButtonLabel'),
              }}
              onChange={(newValue: string | null) => {
                setSelectedDate(newValue ?? '');
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button 
          variant="secondary"
          onClick={handleClose}
          size="lg">
          {t('cancel')}
        </Button>
        <Button 
          variant="primary"
          onClick={handleConfirm}
          size="lg"
          disabled={!selectedDate}>
          {getConfirmButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DateSelectionModal;